import type { QuoteDocument, OrderItem, LineItem, QuoteFileVisibility, PresentationOptionBase } from 'paintscout'
import { uuid } from '../../../util'
import { getQuoteOptions } from '../../../quote/util/get-options'

/**
 * Sample terms for the preview quote, so "View Terms" shows what a real painting contract looks
 * like. No leading "Terms" heading: PresentationTermsDialog already titles the dialog "Terms".
 */
export const PREVIEW_TERMS_CONTENT = [
  '<h3>Scope of Work</h3>',
  '<p>We will supply all labor, materials and equipment to complete the work described in this estimate. Any work not listed is excluded unless added in writing.</p>',
  '<h3>Preparation</h3>',
  '<p>Surfaces will be cleaned, scraped, sanded, caulked and spot-primed as needed. Furniture and fixtures will be moved or covered, and floors protected with drop cloths.</p>',
  '<h3>Materials</h3>',
  '<p>Paint products and colors are as listed in this estimate. Colors must be confirmed before work begins; changes after work starts may add cost.</p>',
  '<h3>Payment</h3>',
  '<ul>',
  '<li>A 30% deposit is due on acceptance to book your start date.</li>',
  '<li>The balance is due on completion, after the final walkthrough.</li>',
  '<li>We accept credit card, bank transfer and cheque.</li>',
  '</ul>',
  '<h3>Changes to the Work</h3>',
  '<p>Changes requested after acceptance will be priced and agreed in writing before the work is done.</p>',
  '<h3>Weather &amp; Scheduling</h3>',
  '<p>Exterior work depends on suitable weather. If rain or low temperatures delay the job, we will reschedule to the next available day.</p>',
  '<h3>Warranty</h3>',
  '<p>Our workmanship is guaranteed for 2 years against peeling, blistering and flaking. The warranty does not cover damage from impact, moisture or normal wear.</p>',
  '<h3>Acceptance</h3>',
  '<p>This estimate is valid for 30 days. By accepting, you agree to these terms.</p>'
].join('')

export function getPresentationPreviewQuote(quote?: Partial<QuoteDocument>): QuoteDocument {
  const lineItems = [
    {
      key: uuid(),
      name: 'First Line Item',
      description: 'This is the line item description.',
      price: 1234.5,
      show: true,
      quantity: 1,
      showPrice: true,
      show_price: true,
      use_total: true,
      images: ['house-1', 'house-2']
    }
  ] as LineItem[]
  const lineItemOptions = [
    {
      key: uuid(),
      name: 'Second Line Item',
      description: 'This is the line item description.',
      price: 1234.5,
      show: true,
      show_price: true,
      use_total: false,
      images: ['house-3']
    }
  ]
  const defaultQuote = {
    _id: 'preview',
    type: 'quote' as const,
    name: '',
    number: 12_345,
    archived: false,

    contact: {
      id: uuid(),
      rev: '1',
      type: 'contact' as const,
      created_by: '',
      contact_type: 'contact' as const,
      created_date: Date.now(),
      updated_date: Date.now(),
      first_name: 'Bob',
      last_name: 'Johnson',
      phone_number: '222.333.4444',
      email: 'bob.johnson@example.com'
    },
    owner: {
      email: 'hugh.green@example.com',
      firstName: 'Hugh',
      lastName: 'Green'
    },
    int_ext: {
      value: 'int'
    },
    files: {
      'house-1': {
        key: 'house-1',
        type: 'image',
        cloudinaryPublicId: 'samples/houses/house-1',
        visibility: 'visible' as QuoteFileVisibility
      },
      'house-2': {
        key: 'house-2',
        type: 'image',
        cloudinaryPublicId: 'samples/houses/house-2',
        visibility: 'visible' as QuoteFileVisibility
      },
      'house-3': {
        key: 'house-3',
        type: 'image',
        cloudinaryPublicId: 'samples/houses/house-3',
        visibility: 'visible' as QuoteFileVisibility
      }
    },
    order: {
      area: [...lineItems, ...lineItemOptions].map((item) => ({ type: 'line_item', key: item.key })) as OrderItem[],
      substrate: [] as OrderItem[]
    },
    areas: {},
    lineItems: [...lineItems, ...lineItemOptions].reduce((item: LineItem, acc: any) => {
      acc[item.key] = item
      return acc
    }, {}),
    quote_date: Date.now(),
    hourly_rate: 50,
    created_by: '',
    status: {
      value: 'accepted' as const
    },
    options: getQuoteOptions({}),
    totals: {
      hours: 0,
      prep: 0,
      price: 1234.5,
      total_discount: 123.45,
      after_discount: 1111.05,
      after_tax: 1111.05
    },
    discount_type: { value: 'percent' },
    discount: 10,
    terms: {
      name: '',
      label: '',
      content: PREVIEW_TERMS_CONTENT
    },
    substrates: {},
    dates: {}
  }

  return {
    ...defaultQuote,
    ...(quote ? quote : {})
  }
}

/**
 * The estimate type the presentation editor previews as: the presentation's only estimate type, or
 * undefined when it applies to all or several types (the preview quote then keeps its default type).
 * Lets estimate-type overrides, such as an alternate company logo, show in the editor.
 */
export function getPresentationPreviewQuoteType(
  presentation?: Pick<PresentationOptionBase, 'quoteTypes'>
): string | undefined {
  const quoteTypes = presentation?.quoteTypes ?? []
  return quoteTypes.length === 1 && quoteTypes[0] !== 'all' ? quoteTypes[0] : undefined
}
